import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useMediaQuery, PHONE_MEDIA_QUERY } from '@mes-amis/super-form';
import Sidebar from './Sidebar';
import CommandBar from './CommandBar';
import GlobalBanner from './GlobalBanner';
import SyncStatusBanner from './SyncStatusBanner';

interface LayoutContentProps {
  children: React.ReactNode;
}

export default function LayoutContent({ children }: LayoutContentProps) {
  const location = useLocation();
  
  const isLoginPage = location.pathname === '/login';
  const isOrganizationSelectionPage = location.pathname === '/select-organization';

  // Hide the main navigation on the super-form document editor at the phone
  // breakpoint and below (PHONE_MEDIA_QUERY = 640px), where super-form provides
  // its own in-page navigation. Both edit routes (saved + draft) are matched.
  const isPhone = useMediaQuery(PHONE_MEDIA_QUERY);
  const isDocumentEdit = Boolean(
    matchPath('/clients/:id/documents/:documentId/edit', location.pathname) ||
      matchPath('/clients/:id/documents/drafts/:localId/edit', location.pathname)
  );
  const showSidebar = !(isPhone && isDocumentEdit);

  // If it's a login page, or organization selection page, don't show the sidebar
  if (isLoginPage || isOrganizationSelectionPage) {
    return (
      <div className="absolute inset-0 flex flex-col bg-gray-50">
        <GlobalBanner />
        <main className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </main>
      </div>
    );
  }
  
  // Default layout with sidebar - App-like structure
  return (
    <div className="absolute inset-0 flex bg-gray-50">
      {/* Fixed Sidebar — hidden on the super-form editor at phone widths */}
      {showSidebar && <Sidebar />}
      {/* Command Bar (Cmd+K) */}
      <CommandBar />
      {/* Main Content Area */}
      <div className="relative flex-1 overflow-hidden bg-gray-50 flex flex-col">
        {/* Sync progress/confirmation overlays the top of the content instead
            of sitting in the flow: it appears on every reconnect and would
            otherwise shift the page (and the field under the user's finger in
            the editor) twice per sync. */}
        <div data-testid="sync-banner-overlay" className="absolute inset-x-0 top-0 z-20 pointer-events-none [&>*]:pointer-events-auto">
          <SyncStatusBanner />
        </div>
        {/* App- and device-wide messages (sync failures, unsynced work, offline
            limit, storage, updates) persist, so they take normal flow and never
            sit over page controls. */}
        <GlobalBanner />
        {/* Scrollable content - pages with h-full (like DocumentEdit) fill this
            exactly and handle their own internal scrolling; other pages overflow
            and this container scrolls them */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
